import React, { useState, useEffect, useMemo, useRef, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getTables,
  createTable,
  updateTable,
  deleteTable,
  generateQR,
  closeSession,
  getSessionOrders,
  getGroups,
  updateGroup,
  removeGroup,
} from '../services/api';
import { QRCodeSVG } from 'qrcode.react';
import ActionMenu from '../components/ActionMenu';
import Icon from '../components/Icon';
import FloorPlanEditor from '../components/floorplan/FloorPlanEditor';
import GroupDetailModal from '../components/floorplan/GroupDetailModal';
import { storage } from '../utils/storage';
import './TablesPage.css';

const LazyQRCode = memo(function LazyQRCode({ value, size }) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '100px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} style={{ width: size, height: size }}>
      {isVisible && <QRCodeSVG value={value} size={size} />}
    </div>
  );
});

function TablesPage() {
  const { t } = useTranslation();
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewTableForm, setShowNewTableForm] = useState(false);
  const [newTable, setNewTable] = useState({ tableNumber: '', seats: 4, location: '' });
  const [selectedQR, setSelectedQR] = useState(null);

  // Edición de mesa
  const [editingTable, setEditingTable] = useState(null);
  const [showEditTableModal, setShowEditTableModal] = useState(false);
  const [editTableForm, setEditTableForm] = useState({
    newTableNumber: '',
    seats: 4,
    location: '',
    status: 'available',
  });

  // Confirmación de eliminación
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [tableToDelete, setTableToDelete] = useState(null);

  // Bulk creation
  const [showRangeForm, setShowRangeForm] = useState(false);
  const [rangeForm, setRangeForm] = useState({ from: '', to: '', seats: 4, location: '' });
  const [rangeCreating, setRangeCreating] = useState(false);

  // Validación de número de mesa
  const [tableNumberError, setTableNumberError] = useState('');
  const TABLE_NUMBER_REGEX = /^[a-zA-Z0-9\-_]+$/;

  const validateTableNumber = (value, excludeTableNumber = null) => {
    if (!value) return '';
    const trimmed = value.trim();
    if (!trimmed) return '';
    if (!TABLE_NUMBER_REGEX.test(trimmed)) {
      return t('tables.invalidTableNumber');
    }
    const isDuplicate = tables.some(
      (table) =>
        String(table.tableNumber).toLowerCase() === trimmed.toLowerCase() &&
        String(table.tableNumber).toLowerCase() !== String(excludeTableNumber ?? '').toLowerCase()
    );
    if (isDuplicate) {
      return t('tables.duplicateTableNumber');
    }
    return '';
  };

  // Cierre de mesa
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [tableToClose, setTableToClose] = useState(null);
  const [closeError, setCloseError] = useState(null);
  const [closeChecking, setCloseChecking] = useState(false);

  // Groups
  const [groups, setGroups] = useState([]);

  // Group detail
  const [selectedGroup, setSelectedGroup] = useState(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterLocation, setFilterLocation] = useState('');

  // Tab: lista vs plano
  const [activeTab, setActiveTab] = useState('list');

  // Estado del restaurante para la impresión
  const [restaurantData, setRestaurantData] = useState(null);

  // Ref for active modal focus trap
  const modalRef = useRef(null);
  // Sesión cuya verificación de impagos está en curso (evita aplicar una
  // respuesta tardía a una mesa distinta si el modal se reabre).
  const closeCheckSessionRef = useRef(null);

  // Focus trap + Escape key for any open modal
  const isAnyModalOpen = showEditTableModal || showDeleteConfirm || showCloseConfirm || selectedQR;

  const handleModalKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') {
        if (showEditTableModal) setShowEditTableModal(false);
        else if (showDeleteConfirm) setShowDeleteConfirm(false);
        else if (showCloseConfirm) setShowCloseConfirm(false);
        else if (selectedQR) setSelectedQR(null);
        return;
      }
      if (e.key === 'Tab' && modalRef.current) {
        const focusable = modalRef.current.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    },
    [showEditTableModal, showDeleteConfirm, showCloseConfirm, selectedQR]
  );

  useEffect(() => {
    if (isAnyModalOpen) {
      document.addEventListener('keydown', handleModalKeyDown);
      // Focus the first focusable element in the modal
      requestAnimationFrame(() => {
        if (modalRef.current) {
          const focusable = modalRef.current.querySelectorAll(
            'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
          );
          if (focusable.length > 0) focusable[0].focus();
        }
      });
      return () => document.removeEventListener('keydown', handleModalKeyDown);
    }
  }, [isAnyModalOpen, handleModalKeyDown]);

  useEffect(() => {
    // Cargar datos del restaurante desde localStorage
    const restaurantId = storage.get('selectedRestaurantId');
    const cachedRestaurant = storage.get(`restaurant_${restaurantId}`);
    if (cachedRestaurant) {
      setRestaurantData(JSON.parse(cachedRestaurant));
    }
  }, []);

  useEffect(() => {
    loadTables();
  }, []);

  const loadTables = async () => {
    try {
      const [tablesResponse, groupsResponse] = await Promise.all([
        getTables(),
        getGroups().catch(() => ({ groups: [] })),
      ]);
      const activeTables = (tablesResponse.tables || []).filter(
        (table) => table.status !== 'deleted'
      );
      setTables(activeTables);
      setGroups(groupsResponse.groups || []);
    } catch (error) {
      console.error('Error loading tables:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTable = async (e) => {
    e.preventDefault();
    const trimmedNumber = newTable.tableNumber.trim();
    const error = validateTableNumber(trimmedNumber);
    if (error) {
      setTableNumberError(error);
      return;
    }
    try {
      await createTable({ ...newTable, tableNumber: trimmedNumber });
      setNewTable({ tableNumber: '', seats: 4, location: '' });
      setTableNumberError('');
      setShowNewTableForm(false);
      loadTables();
    } catch (error) {
      alert(t('tables.createError'));
    }
  };

  const handleCreateRange = async (e) => {
    e.preventDefault();
    const from = parseInt(rangeForm.from);
    const to = parseInt(rangeForm.to);
    if (isNaN(from) || isNaN(to)) return;
    if (from > to) return;
    setRangeCreating(true);
    let created = 0;
    const total = to - from + 1;
    for (let i = from; i <= to; i++) {
      try {
        await createTable({
          tableNumber: String(i),
          seats: rangeForm.seats || 4,
          location: rangeForm.location || '',
        });
        created++;
      } catch {
        // Table may already exist — skip
      }
    }
    setRangeCreating(false);
    setShowRangeForm(false);
    setRangeForm({ from: '', to: '', seats: 4, location: '' });
    loadTables();
    if (created === total) {
      alert(t('tables.rangeCreated', { count: created }));
    } else if (created > 0) {
      alert(t('tables.rangePartialError', { created, total }));
    } else {
      alert(t('tables.rangePartialError', { created: 0, total }));
    }
  };

  const handleGenerateQR = async (tableNumber) => {
    try {
      const response = await generateQR(tableNumber);
      setSelectedQR(response);
      loadTables();
    } catch (error) {
      alert(t('tables.qrError'));
    }
  };

  // Editar mesa
  const handleOpenEditTable = (table) => {
    setEditingTable(table);
    setEditTableForm({
      newTableNumber: table.tableNumber,
      seats: table.seats || 4,
      location: table.location || '',
      status: table.status || 'available',
    });
    setTableNumberError('');
    setShowEditTableModal(true);
  };

  const handleUpdateTable = async (e) => {
    e.preventDefault();
    const trimmedNumber = editTableForm.newTableNumber.trim();
    const error = validateTableNumber(trimmedNumber, editingTable.tableNumber);
    if (error) {
      setTableNumberError(error);
      return;
    }
    try {
      await updateTable(editingTable.tableNumber, {
        ...editTableForm,
        newTableNumber: trimmedNumber,
      });
      setShowEditTableModal(false);
      setEditingTable(null);
      setTableNumberError('');
      loadTables();
    } catch (error) {
      alert(t('tables.updateError') + ': ' + (error.response?.data?.error || error.message));
    }
  };

  // Eliminar mesa
  const handleDeleteTableClick = (table) => {
    setTableToDelete(table);
    setShowDeleteConfirm(true);
  };

  const handleDeleteTable = async () => {
    try {
      await deleteTable(tableToDelete.tableNumber);
      setShowDeleteConfirm(false);
      setTableToDelete(null);
      loadTables();
    } catch (error) {
      alert(t('tables.deleteError') + ': ' + (error.response?.data?.error || error.message));
    }
  };

  const handleCloseTableClick = async (table) => {
    setTableToClose(table);
    setCloseError(null);
    setShowCloseConfirm(true);
    // Verificación proactiva: si la mesa tiene pedidos impagos, mostramos la
    // advertencia "Forzar cierre" ANTES del confirm genérico (no dependemos del
    // round-trip al cerrar, que puede no advertir si la sesión perdió sus orders).
    if (!table.currentSessionId) return;
    const sessionId = table.currentSessionId;
    closeCheckSessionRef.current = sessionId;
    setCloseChecking(true);
    try {
      const data = await getSessionOrders(sessionId);
      if (closeCheckSessionRef.current !== sessionId) return; // modal reabierto en otra mesa
      const unpaidOrders = (data.orders || []).filter((o) => o.paymentStatus !== 'paid');
      if (unpaidOrders.length > 0) {
        setCloseError({
          unpaidCount: unpaidOrders.length,
          unpaidTotal:
            typeof data.totalUnpaid === 'number'
              ? data.totalUnpaid
              : unpaidOrders.reduce((sum, o) => sum + (o.total || 0), 0),
        });
      }
    } catch {
      // Si falla la verificación previa, el backend igual valida al cerrar
      // (devuelve 400 con unpaidCount) — no bloqueamos el flujo.
    } finally {
      if (closeCheckSessionRef.current === sessionId) setCloseChecking(false);
    }
  };

  const handleCloseTable = async (forceClose = false) => {
    if (!tableToClose?.currentSessionId) return;
    try {
      await closeSession(tableToClose.currentSessionId, forceClose);
      setShowCloseConfirm(false);
      setTableToClose(null);
      setCloseError(null);
      loadTables();
    } catch (error) {
      const data = error.response?.data;
      if (data?.unpaidCount && !forceClose) {
        setCloseError(data);
      } else {
        alert(t('common.error') + ': ' + (data?.error || error.message));
      }
    }
  };

  // Imprimir QR con datos del restaurante
  const handlePrintQR = (table) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert(
        t(
          'tables.printBlockedError',
          'No se pudo abrir la ventana de impresión. Verifica que no esté bloqueada.'
        )
      );
      return;
    }

    const restaurantName = restaurantData?.name || 'Restaurante';
    const restaurantAddress = restaurantData?.address || '';
    const restaurantPhone = restaurantData?.phone || '';

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Mesa ${table.tableNumber} - QR</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: 'Segoe UI', Arial, sans-serif;
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
            background: #f5f5f5;
          }
          .print-container {
            background: white;
            padding: 40px;
            border-radius: 16px;
            box-shadow: 0 4px 20px rgba(0,0,0,0.1);
            text-align: center;
            max-width: 400px;
            width: 100%;
          }
          .restaurant-name {
            font-size: 28px;
            font-weight: 700;
            color: #333;
            margin-bottom: 8px;
          }
          .restaurant-info {
            font-size: 14px;
            color: #666;
            margin-bottom: 24px;
            line-height: 1.5;
          }
          .table-number {
            font-size: 48px;
            font-weight: 800;
            color: #2563eb;
            margin-bottom: 8px;
          }
          .table-label {
            font-size: 18px;
            color: #666;
            text-transform: uppercase;
            letter-spacing: 2px;
            margin-bottom: 24px;
          }
          .qr-wrapper {
            background: white;
            padding: 20px;
            border-radius: 12px;
            display: inline-block;
            border: 3px solid #e5e5e5;
            margin-bottom: 24px;
          }
          .qr-wrapper svg {
            display: block;
          }
          .qr-code-text {
            font-size: 11px;
            color: #aaa;
            font-family: monospace;
            margin-bottom: 16px;
            word-break: break-all;
          }
          .scan-text {
            font-size: 16px;
            color: #333;
            font-weight: 600;
            margin-bottom: 8px;
          }
          .scan-subtext {
            font-size: 13px;
            color: #888;
          }
          .provider-logo {
            margin-top: 32px;
            padding-top: 20px;
            border-top: 1px solid #e5e5e5;
          }
          .provider-logo span {
            font-size: 11px;
            color: #aaa;
            display: block;
            margin-bottom: 4px;
          }
          .provider-name {
            font-size: 14px;
            font-weight: 600;
            color: #666;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
          }
          .provider-icon {
            width: 20px;
            height: 20px;
            background: linear-gradient(135deg, #2563eb, #7c3aed);
            border-radius: 4px;
          }
          @media print {
            body { background: white; }
            .print-container {
              box-shadow: none;
              padding: 20px;
            }
          }
        </style>
      </head>
      <body>
        <div class="print-container">
          <div class="restaurant-name">${restaurantName}</div>
          ${
            restaurantAddress || restaurantPhone
              ? `
            <div class="restaurant-info">
              ${restaurantAddress ? `<div>${restaurantAddress}</div>` : ''}
              ${restaurantPhone ? `<div>${restaurantPhone}</div>` : ''}
            </div>
          `
              : ''
          }
          <div class="table-number">${table.tableNumber}</div>
          <div class="table-label">${t('tables.table', 'Mesa')}</div>
          <div class="qr-wrapper" id="qr-container"></div>
          <div class="qr-code-text">${table.qrCode || ''}</div>
          <div class="scan-text">${t('tables.scanToOrder', 'Escanea para ver el menú')}</div>
          <div class="scan-subtext">${t('tables.scanSubtext', 'y hacer tu pedido desde tu celular')}</div>
          <div class="provider-logo">
            <span>${t('tables.poweredBy', 'Powered by')}</span>
            <div class="provider-name">
              <div class="provider-icon"></div>
              Menu Digital
            </div>
          </div>
        </div>
        <script>
          // Renderizar QR usando canvas
          const qrContainer = document.getElementById('qr-container');
          const qrUrl = '${table.qrUrl}';

          // Crear un elemento temporal para obtener el SVG
          const tempDiv = document.createElement('div');
          document.body.appendChild(tempDiv);

          // Usar la librería QRCode si está disponible, o mostrar placeholder
          const img = document.createElement('img');
          img.src = 'https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=' + encodeURIComponent(qrUrl);
          img.style.width = '200px';
          img.style.height = '200px';
          qrContainer.appendChild(img);

          // Auto-imprimir después de cargar
          img.onload = function() {
            setTimeout(function() {
              window.print();
            }, 500);
          };
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleGroupUpdate = async (groupId, data) => {
    try {
      await updateGroup(groupId, data);
      setSelectedGroup(null);
      loadTables();
    } catch (error) {
      const msg = error.response?.data?.error || error.message;
      alert(msg);
    }
  };

  const handleGroupDissolve = async (groupId) => {
    try {
      await removeGroup(groupId);
      setSelectedGroup(null);
      loadTables();
    } catch (error) {
      const msg = error.response?.data?.error || error.message;
      alert(msg);
    }
  };

  const locations = useMemo(() => {
    const locs = tables.map((t) => t.location).filter(Boolean);
    return [...new Set(locs)].sort();
  }, [tables]);

  const filteredTables = useMemo(() => {
    return tables.filter((table) => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (!String(table.tableNumber).toLowerCase().includes(q)) {
          return false;
        }
      }
      if (filterStatus && table.status !== filterStatus) {
        return false;
      }
      if (filterLocation && table.location !== filterLocation) {
        return false;
      }
      return true;
    });
  }, [tables, searchQuery, filterStatus, filterLocation]);

  if (loading) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  return (
    <div className="tables-page">
      <div className="page-header">
        <h1>{t('tables.title')}</h1>
        <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              setShowRangeForm(!showRangeForm);
              setShowNewTableForm(false);
            }}
          >
            <Icon name="playlist_add" />
            {t('tables.createRange')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              setShowNewTableForm(!showNewTableForm);
              setShowRangeForm(false);
            }}
          >
            + {t('tables.newTable')}
          </button>
        </div>
      </div>

      <div className="tables-tab-bar">
        <button
          type="button"
          className={`tables-tab ${activeTab === 'list' ? 'tables-tab--active' : ''}`}
          onClick={() => setActiveTab('list')}
        >
          <Icon name="view_list" />
          {t('tables.listView', 'Lista')}
        </button>
        <button
          type="button"
          className={`tables-tab ${activeTab === 'plan' ? 'tables-tab--active' : ''}`}
          onClick={() => setActiveTab('plan')}
        >
          <Icon name="grid_view" />
          {t('tables.floorPlanView', 'Plano')}
        </button>
      </div>

      {activeTab === 'list' && (
        <div className="tables-filters">
          <input
            type="text"
            className="tables-filters__search"
            placeholder={t('tables.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <select
            className="tables-filters__select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            aria-label={t('tables.filterByStatus')}
          >
            <option value="">{t('tables.allStatuses')}</option>
            <option value="available">{t('tables.available')}</option>
            <option value="occupied">{t('tables.occupied')}</option>
            <option value="reserved">{t('tables.reserved')}</option>
            <option value="maintenance">{t('tables.maintenance')}</option>
          </select>
          {locations.length > 0 && (
            <select
              className="tables-filters__select"
              value={filterLocation}
              onChange={(e) => setFilterLocation(e.target.value)}
              aria-label={t('tables.filterByLocation')}
            >
              <option value="">{t('tables.allLocations')}</option>
              {locations.map((loc) => (
                <option key={loc} value={loc}>
                  {loc}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      {showRangeForm && (
        <div className="card">
          <h2>{t('tables.createRangeTitle')}</h2>
          <form onSubmit={handleCreateRange}>
            <div
              style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-md)' }}
            >
              <div className="form-group">
                <label htmlFor="range-from">{t('tables.rangeFrom')}</label>
                <input
                  id="range-from"
                  type="number"
                  min={1}
                  value={rangeForm.from}
                  onChange={(e) => setRangeForm({ ...rangeForm, from: e.target.value })}
                  placeholder={t('tables.rangeFromPlaceholder')}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="range-to">{t('tables.rangeTo')}</label>
                <input
                  id="range-to"
                  type="number"
                  min={1}
                  value={rangeForm.to}
                  onChange={(e) => setRangeForm({ ...rangeForm, to: e.target.value })}
                  placeholder={t('tables.rangeToPlaceholder')}
                  required
                />
              </div>
            </div>
            <div
              style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-md)' }}
            >
              <div className="form-group">
                <label htmlFor="range-seats">{t('tables.defaultSeats')}</label>
                <input
                  id="range-seats"
                  type="number"
                  min={1}
                  value={rangeForm.seats}
                  onChange={(e) => {
                    const val = parseInt(e.target.value);
                    setRangeForm({ ...rangeForm, seats: isNaN(val) ? '' : Math.max(1, val) });
                  }}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="range-location">{t('tables.defaultLocation')}</label>
                <input
                  id="range-location"
                  type="text"
                  value={rangeForm.location}
                  onChange={(e) => setRangeForm({ ...rangeForm, location: e.target.value })}
                  placeholder={t('tables.locationPlaceholder')}
                />
              </div>
            </div>
            {rangeForm.from &&
              rangeForm.to &&
              parseInt(rangeForm.from) <= parseInt(rangeForm.to) && (
                <p
                  style={{
                    color: 'var(--text-secondary)',
                    fontSize: 'var(--text-sm)',
                    marginBottom: 'var(--space-md)',
                  }}
                >
                  {t('tables.rangePreview', {
                    count: parseInt(rangeForm.to) - parseInt(rangeForm.from) + 1,
                    from: rangeForm.from,
                    to: rangeForm.to,
                  })}
                </p>
              )}
            {rangeForm.from &&
              rangeForm.to &&
              parseInt(rangeForm.from) > parseInt(rangeForm.to) && (
                <p
                  style={{
                    color: 'var(--color-danger)',
                    fontSize: 'var(--text-sm)',
                    marginBottom: 'var(--space-md)',
                  }}
                >
                  {t('tables.rangeInvalid')}
                </p>
              )}
            <div className="form-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setShowRangeForm(false)}
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={
                  rangeCreating ||
                  !rangeForm.from ||
                  !rangeForm.to ||
                  parseInt(rangeForm.from) > parseInt(rangeForm.to)
                }
              >
                {rangeCreating ? t('tables.rangeCreating') : t('tables.createRange')}
              </button>
            </div>
          </form>
        </div>
      )}

      {showNewTableForm && (
        <div className="card">
          <h2>{t('tables.newTable')}</h2>
          <form onSubmit={handleCreateTable}>
            <div className="form-group">
              <label htmlFor="new-table-number">{t('tables.tableNumber')}</label>
              <input
                id="new-table-number"
                type="text"
                value={newTable.tableNumber}
                onChange={(e) => {
                  setNewTable({ ...newTable, tableNumber: e.target.value });
                  setTableNumberError(validateTableNumber(e.target.value));
                }}
                placeholder={t('tables.numberPlaceholder')}
                required
              />
              {tableNumberError && (
                <small className="form-hint" style={{ color: 'var(--color-danger)' }}>
                  {tableNumberError}
                </small>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="new-table-seats">{t('tables.seats')}</label>
              <input
                id="new-table-seats"
                type="number"
                min={1}
                value={newTable.seats}
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  setNewTable({ ...newTable, seats: isNaN(val) ? '' : Math.max(1, val) });
                }}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="new-table-location">{t('tables.location')}</label>
              <input
                id="new-table-location"
                type="text"
                value={newTable.location}
                onChange={(e) => setNewTable({ ...newTable, location: e.target.value })}
                placeholder={t('tables.locationPlaceholder')}
              />
            </div>
            <div className="form-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setShowNewTableForm(false)}
              >
                {t('common.cancel')}
              </button>
              <button type="submit" className="btn btn-primary">
                {t('tables.create')}
              </button>
            </div>
          </form>
        </div>
      )}

      {activeTab === 'plan' ? (
        <FloorPlanEditor
          tables={tables}
          onTablesChange={loadTables}
          onEditTable={handleOpenEditTable}
          onGenerateQR={handleGenerateQR}
          onCloseTable={handleCloseTableClick}
          onShowQR={(table) => setSelectedQR(table)}
          onPrintQR={handlePrintQR}
        />
      ) : (
        <div className={`tables-grid${filteredTables.length < 3 ? ' tables-grid--few' : ''}`}>
          {filteredTables.length === 0 ? (
            <div className="empty-state">
              <p>{tables.length === 0 ? t('tables.emptyState') : t('tables.noResults')}</p>
            </div>
          ) : (
            <>
              {filteredTables.map((table) => {
                const tableGroup = groups.find((g) =>
                  g.tableNumbers?.includes(String(table.tableNumber))
                );
                return (
                  <div
                    key={table.tableNumber}
                    className={`table-card card table-card--${table.status || 'available'}`}
                  >
                    <div className="table-card-header">
                      <h2>
                        {t('tables.table')} {table.tableNumber}
                        {tableGroup && (
                          <span
                            className="table-group-badge table-group-badge--clickable"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedGroup(tableGroup);
                            }}
                            title={t('tables.groupDetail')}
                          >
                            {t('tables.group')}: {tableGroup.name}
                          </span>
                        )}
                      </h2>
                      <ActionMenu
                        actions={[
                          {
                            label: table.qrCode
                              ? t('tables.regenerateQR', 'Regenerar QR')
                              : t('tables.generateQR', 'Generar QR'),
                            icon: table.qrCode ? 'refresh' : 'qr_code',
                            onClick: () => handleGenerateQR(table.tableNumber),
                            variant: 'default',
                          },
                          ...(table.qrCode
                            ? [
                                {
                                  label: t('tables.printQR', 'Imprimir QR'),
                                  icon: 'print',
                                  onClick: () => handlePrintQR(table),
                                  variant: 'default',
                                },
                              ]
                            : []),
                          ...(table.currentSessionId
                            ? [
                                {
                                  label: t('tables.closeTable', 'Cerrar mesa'),
                                  icon: 'logout',
                                  onClick: () => handleCloseTableClick(table),
                                  variant: 'warning',
                                },
                              ]
                            : []),
                          {
                            label: t('common.edit'),
                            icon: 'edit',
                            onClick: () => handleOpenEditTable(table),
                            variant: 'default',
                          },
                          {
                            label: t('common.delete'),
                            icon: 'delete',
                            onClick: () => handleDeleteTableClick(table),
                            variant: 'danger',
                            disabled:
                              table.status !== 'available' && table.status !== 'maintenance',
                          },
                        ]}
                      />
                    </div>
                    <div className="table-card-body">
                      <div className="table-info">
                        <p>
                          <Icon name="chair" /> {table.seats} {t('tables.seatsLabel')}
                        </p>
                        {table.location && (
                          <p>
                            <Icon name="place" /> {table.location}
                          </p>
                        )}
                        <span className={`status-badge table-status-badge ${table.status}`}>
                          <span className="status-badge-dot"></span>
                          {table.status === 'available'
                            ? t('tables.available')
                            : table.status === 'occupied'
                              ? t('tables.occupied')
                              : table.status === 'reserved'
                                ? t('tables.reserved')
                                : table.status === 'maintenance'
                                  ? t('tables.maintenance')
                                  : table.status}
                        </span>
                      </div>
                      {table.qrCode ? (
                        <div className="qr-section">
                          <div
                            className="qr-code-wrapper clickable"
                            onClick={() => setSelectedQR(table)}
                            title={t('tables.viewQR', 'Ver QR')}
                          >
                            <LazyQRCode value={table.qrUrl} size={80} />
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-primary btn-small"
                          onClick={() => handleGenerateQR(table.tableNumber)}
                        >
                          <Icon name="qr_code" />
                          {t('tables.generateQR')}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
              {filteredTables.length > 0 &&
                filteredTables.length < 3 &&
                !searchQuery &&
                !filterStatus &&
                !filterLocation && (
                  <div
                    className="table-card-cta card"
                    onClick={() => setShowNewTableForm(true)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') setShowNewTableForm(true);
                    }}
                  >
                    <div className="table-card-cta__icon">
                      <Icon name="add_circle_outline" />
                    </div>
                    <p className="table-card-cta__text">{t('tables.addMoreTables')}</p>
                    <span className="table-card-cta__action">{t('tables.addMoreTablesCta')}</span>
                  </div>
                )}
            </>
          )}
        </div>
      )}

      {/* Modal QR - Compact */}
      {selectedQR && (
        <div
          className="qr-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="qr-modal-title"
          onClick={() => setSelectedQR(null)}
        >
          <div
            className="qr-modal-content compact"
            ref={modalRef}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="modal-close"
              onClick={() => setSelectedQR(null)}
              aria-label={t('common.close')}
            >
              ×
            </button>
            <h2 id="qr-modal-title">
              {t('tables.table')} {selectedQR.tableNumber}
            </h2>
            <QRCodeSVG value={selectedQR.qrUrl} size={200} />
            <a
              href={selectedQR.qrUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="qr-url-link"
            >
              <Icon name="open_in_new" />
              {t('tables.openMenu', 'Abrir menú')}
            </a>
            <div className="qr-modal-actions">
              <button
                type="button"
                className="btn btn-secondary btn-small"
                onClick={async () => {
                  await handleGenerateQR(selectedQR.tableNumber);
                }}
              >
                <Icon name="refresh" size={16} />
                {t('tables.regenerateQR', 'Regenerar QR')}
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-small"
                onClick={() => setSelectedQR(null)}
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Editar Mesa */}
      {showEditTableModal && editingTable && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="edit-table-modal-title"
          onClick={() => setShowEditTableModal(false)}
        >
          <div className="modal-content" ref={modalRef} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 id="edit-table-modal-title">
                {t('tables.editTable')} {editingTable.tableNumber}
              </h2>
              <button
                type="button"
                className="modal-close"
                onClick={() => setShowEditTableModal(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleUpdateTable}>
              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="edit-table-number">{t('tables.tableNumber')}</label>
                  <input
                    id="edit-table-number"
                    type="text"
                    value={editTableForm.newTableNumber}
                    onChange={(e) => {
                      setEditTableForm({ ...editTableForm, newTableNumber: e.target.value });
                      setTableNumberError(
                        validateTableNumber(e.target.value, editingTable.tableNumber)
                      );
                    }}
                    required
                  />
                  {tableNumberError ? (
                    <small className="form-hint" style={{ color: 'var(--color-danger)' }}>
                      {tableNumberError}
                    </small>
                  ) : (
                    <small className="form-hint">{t('tables.numberChangeHint')}</small>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="edit-table-seats">{t('tables.seats')}</label>
                  <input
                    id="edit-table-seats"
                    type="number"
                    min={1}
                    value={editTableForm.seats}
                    onChange={(e) => {
                      const val = parseInt(e.target.value);
                      setEditTableForm({
                        ...editTableForm,
                        seats: isNaN(val) ? '' : Math.max(1, val),
                      });
                    }}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="edit-table-location">{t('tables.location')}</label>
                  <input
                    id="edit-table-location"
                    type="text"
                    value={editTableForm.location}
                    onChange={(e) =>
                      setEditTableForm({ ...editTableForm, location: e.target.value })
                    }
                    placeholder={t('tables.locationPlaceholder')}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="edit-table-status">{t('tables.status')}</label>
                  <select
                    id="edit-table-status"
                    value={editTableForm.status}
                    onChange={(e) => setEditTableForm({ ...editTableForm, status: e.target.value })}
                    required
                  >
                    <option value="available">{t('tables.available')}</option>
                    <option value="occupied">{t('tables.occupied')}</option>
                    <option value="reserved">{t('tables.reserved')}</option>
                    <option value="maintenance">{t('tables.maintenance')}</option>
                  </select>
                </div>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => setShowEditTableModal(false)}
                >
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn btn-primary">
                  {t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Confirmar Eliminar Mesa */}
      {showDeleteConfirm && tableToDelete && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-table-modal-title"
          onClick={() => setShowDeleteConfirm(false)}
        >
          <div
            className="modal-content modal-small"
            ref={modalRef}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2 id="delete-table-modal-title">{t('tables.deleteTable')}</h2>
              <button
                type="button"
                className="modal-close"
                onClick={() => setShowDeleteConfirm(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>{t('tables.deleteConfirmation', { tableNumber: tableToDelete.tableNumber })}</p>
              {tableToDelete.currentSessionId ? (
                <p className="warning-text">
                  <Icon name="warning" /> {t('tables.activeSessionWarning')}
                </p>
              ) : tableToDelete.status !== 'available' && tableToDelete.status !== 'maintenance' ? (
                <p className="warning-text">
                  <Icon name="warning" /> {t('tables.cannotDeleteNonAvailable')}
                </p>
              ) : (
                <p className="warning-text">{t('tables.deleteWarning')}</p>
              )}
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setShowDeleteConfirm(false)}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDeleteTable}
                disabled={
                  tableToDelete.status !== 'available' && tableToDelete.status !== 'maintenance'
                }
              >
                {t('tables.confirmDelete')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Modal Cerrar Mesa */}
      {showCloseConfirm && tableToClose && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="close-table-modal-title"
          onClick={() => setShowCloseConfirm(false)}
        >
          <div
            className="modal-content modal-small"
            ref={modalRef}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2 id="close-table-modal-title">
                {t('tables.closeTable', 'Cerrar mesa')} {tableToClose.tableNumber}
              </h2>
              <button
                type="button"
                className="modal-close"
                onClick={() => setShowCloseConfirm(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {closeError ? (
                <>
                  <p className="warning-text">
                    <Icon name="warning" />{' '}
                    {t('tables.unpaidWarning', { count: closeError.unpaidCount })}
                  </p>
                  <p>
                    {t('common.total')}: ${closeError.unpaidTotal?.toFixed(2)}
                  </p>
                </>
              ) : closeChecking ? (
                <p>{t('tables.checkingOrders', 'Verificando pedidos…')}</p>
              ) : (
                <p>{t('tables.closeConfirmation', '¿Cerrar esta mesa y liberar la sesión?')}</p>
              )}
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setShowCloseConfirm(false)}
              >
                {t('common.cancel')}
              </button>
              {closeError ? (
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => handleCloseTable(true)}
                >
                  {t('tables.forceClose', 'Forzar cierre')}
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-warning"
                  onClick={() => handleCloseTable(false)}
                  disabled={closeChecking}
                >
                  {t('tables.closeTable', 'Cerrar mesa')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Group Detail Modal */}
      {selectedGroup && (
        <GroupDetailModal
          group={selectedGroup}
          tables={tables}
          onUpdate={handleGroupUpdate}
          onDissolve={handleGroupDissolve}
          onClose={() => setSelectedGroup(null)}
        />
      )}
    </div>
  );
}

export default TablesPage;
